import { Injectable } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { ApiEndpointsService } from '../services/api-endpoints.service';
import { ApiHttpService } from '../services/api-http.service';
import {
  ChatListQuery,
  ChatListResponse,
  ChatDeleteResponse,
  ChatFileDownloadRequest,
  ChatFileDownloadResponse,
  ChatFileUploadRequest,
  ChatFileUploadResponse,
  ChatLeaveResponse,
  ChatMessageFileInputDto,
  ChatMuteResponse,
  ChatRemoveMemberResponse,
  ChatReactionEmoji,
  ChatMessagesQuery,
  ChatMessagesResponse,
  ChatReadResponse,
  ChatSummaryDto,
  ChatUnreadResponse,
  ChatUnreadCountResponse,
  CreateDirectChatDto,
  CreateDirectChatResponse,
  CreateGroupChatDto,
  CreateSupportChatDto,
  EditChatMessageDto,
  InviteChatMembersDto,
  SupportAssignConversationDto,
  SupportEligibleOwnersResponse,
  SupportInboxQuery,
  SupportStatusDto,
  SupportWorkflowDto,
  SupportWorkflowEventsResponse,
  UpdateChatDto,
  SendChatMessageDto,
  ChatMessageDto,
} from './chat.dto';

/** REST client for ACE chat endpoints. AceTokenInterceptor supplies auth. */
@Injectable({ providedIn: 'root' })
export class ChatService {
  constructor(
    private readonly apiHttpService: ApiHttpService,
    private readonly apiEndpointsService: ApiEndpointsService
  ) {}

  listChats(query: ChatListQuery = {}): Observable<ChatListResponse> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getChats(query))
      .pipe(map(response => this.unwrap<ChatListResponse>(response)));
  }

  listSpikeSupport(
    query: SupportInboxQuery = {}
  ): Observable<ChatListResponse> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getSpikeSupportChats(query))
      .pipe(map(response => this.unwrap<ChatListResponse>(response)));
  }

  getChat(chatId: string): Observable<ChatSummaryDto> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getChat(chatId))
      .pipe(map(response => this.unwrap<ChatSummaryDto>(response)));
  }

  updateChat(chatId: string, dto: UpdateChatDto): Observable<ChatSummaryDto> {
    return this.apiHttpService
      .put(this.apiEndpointsService.updateChat(chatId), dto)
      .pipe(map(response => this.unwrap<ChatSummaryDto>(response)));
  }

  deleteChat(chatId: string): Observable<ChatDeleteResponse> {
    return this.apiHttpService
      .delete(this.apiEndpointsService.deleteChat(chatId))
      .pipe(map(response => this.unwrap<ChatDeleteResponse>(response)));
  }

  createDirectChat(
    dto: CreateDirectChatDto
  ): Observable<CreateDirectChatResponse> {
    return this.apiHttpService
      .post(this.apiEndpointsService.createDirectChat(), dto)
      .pipe(map(response => this.unwrap<CreateDirectChatResponse>(response)));
  }

  createGroupChat(dto: CreateGroupChatDto): Observable<ChatSummaryDto> {
    return this.apiHttpService
      .post(this.apiEndpointsService.createGroupChat(), dto)
      .pipe(map(response => this.unwrap<ChatSummaryDto>(response)));
  }

  createSupportChat(dto: CreateSupportChatDto): Observable<ChatSummaryDto> {
    return this.apiHttpService
      .post(this.apiEndpointsService.createSupportChat(), dto)
      .pipe(map(response => this.unwrap<ChatSummaryDto>(response)));
  }

  getSupportWorkflow(chatId: string): Observable<SupportWorkflowDto> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getSupportWorkflow(chatId))
      .pipe(map(response => this.unwrap<SupportWorkflowDto>(response)));
  }

  getEligibleSupportOwners(
    chatId: string
  ): Observable<SupportEligibleOwnersResponse> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getEligibleSupportOwners(chatId))
      .pipe(
        map(response => this.unwrap<SupportEligibleOwnersResponse>(response))
      );
  }

  getSupportWorkflowEvents(
    chatId: string
  ): Observable<SupportWorkflowEventsResponse> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getSupportWorkflowEvents(chatId))
      .pipe(
        map(response => this.unwrap<SupportWorkflowEventsResponse>(response))
      );
  }

  claimSupportWorkflow(chatId: string): Observable<SupportWorkflowDto> {
    return this.apiHttpService
      .post(this.apiEndpointsService.claimSupportWorkflow(chatId), {})
      .pipe(map(response => this.unwrap<SupportWorkflowDto>(response)));
  }

  assignSupportWorkflow(
    chatId: string,
    dto: SupportAssignConversationDto
  ): Observable<SupportWorkflowDto> {
    return this.apiHttpService
      .put(this.apiEndpointsService.assignSupportWorkflow(chatId), dto)
      .pipe(map(response => this.unwrap<SupportWorkflowDto>(response)));
  }

  updateSupportWorkflowStatus(
    chatId: string,
    dto: SupportStatusDto
  ): Observable<SupportWorkflowDto> {
    return this.apiHttpService
      .put(this.apiEndpointsService.updateSupportWorkflowStatus(chatId), dto)
      .pipe(map(response => this.unwrap<SupportWorkflowDto>(response)));
  }

  getMessages(
    chatId: string,
    query: ChatMessagesQuery = {}
  ): Observable<ChatMessagesResponse> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getChatMessages(chatId, query))
      .pipe(map(response => this.unwrap<ChatMessagesResponse>(response)));
  }

  sendMessage(
    chatId: string,
    dto: SendChatMessageDto
  ): Observable<ChatMessageDto> {
    return this.apiHttpService
      .post(this.apiEndpointsService.sendChatMessage(chatId), dto)
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  markRead(chatId: string, messageId: string): Observable<ChatReadResponse> {
    return this.apiHttpService
      .post(this.apiEndpointsService.markChatRead(chatId), { messageId })
      .pipe(map(response => this.unwrap<ChatReadResponse>(response)));
  }

  markUnread(chatId: string): Observable<ChatUnreadResponse> {
    return this.apiHttpService
      .post(this.apiEndpointsService.markChatUnread(chatId), {})
      .pipe(map(response => this.unwrap<ChatUnreadResponse>(response)));
  }

  editMessage(
    chatId: string,
    messageId: string,
    dto: EditChatMessageDto
  ): Observable<ChatMessageDto> {
    return this.apiHttpService
      .patch(this.apiEndpointsService.editChatMessage(chatId, messageId), dto)
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  setMessageReaction(
    chatId: string,
    messageId: string,
    emoji: ChatReactionEmoji
  ): Observable<ChatMessageDto> {
    return this.apiHttpService
      .put(this.apiEndpointsService.setChatMessageReaction(chatId, messageId), {
        emoji,
      })
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  removeMessageReaction(
    chatId: string,
    messageId: string
  ): Observable<ChatMessageDto> {
    return this.apiHttpService
      .delete(
        this.apiEndpointsService.setChatMessageReaction(chatId, messageId)
      )
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  pinMessage(chatId: string, messageId: string): Observable<ChatMessageDto> {
    return this.apiHttpService
      .put(this.apiEndpointsService.pinChatMessage(chatId, messageId), {})
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  unpinMessage(chatId: string, messageId: string): Observable<ChatMessageDto> {
    return this.apiHttpService
      .delete(this.apiEndpointsService.pinChatMessage(chatId, messageId))
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  deleteMessage(chatId: string, messageId: string): Observable<ChatMessageDto> {
    return this.apiHttpService
      .delete(this.apiEndpointsService.deleteChatMessage(chatId, messageId))
      .pipe(map(response => this.unwrap<ChatMessageDto>(response)));
  }

  muteChat(chatId: string): Observable<ChatMuteResponse> {
    return this.apiHttpService
      .put(this.apiEndpointsService.muteChat(chatId), {})
      .pipe(map(response => this.unwrap<ChatMuteResponse>(response)));
  }

  unmuteChat(chatId: string): Observable<ChatMuteResponse> {
    return this.apiHttpService
      .delete(this.apiEndpointsService.muteChat(chatId))
      .pipe(map(response => this.unwrap<ChatMuteResponse>(response)));
  }

  archiveChat(chatId: string): Observable<ChatLeaveResponse> {
    return this.apiHttpService
      .post(this.apiEndpointsService.archiveChat(chatId), {})
      .pipe(map(response => this.unwrap<ChatLeaveResponse>(response)));
  }

  leaveChat(chatId: string): Observable<ChatLeaveResponse> {
    return this.apiHttpService
      .post(this.apiEndpointsService.leaveChat(chatId), {})
      .pipe(map(response => this.unwrap<ChatLeaveResponse>(response)));
  }

  restoreChat(chatId: string): Observable<ChatSummaryDto> {
    return this.apiHttpService
      .post(this.apiEndpointsService.restoreChat(chatId), {})
      .pipe(map(response => this.unwrap<ChatSummaryDto>(response)));
  }

  inviteMembers(
    chatId: string,
    dto: InviteChatMembersDto
  ): Observable<ChatSummaryDto> {
    return this.apiHttpService
      .post(this.apiEndpointsService.inviteChatMembers(chatId), dto)
      .pipe(map(response => this.unwrap<ChatSummaryDto>(response)));
  }

  removeMember(
    chatId: string,
    userId: string
  ): Observable<ChatRemoveMemberResponse> {
    return this.apiHttpService
      .delete(this.apiEndpointsService.removeChatMember(chatId, userId))
      .pipe(map(response => this.unwrap<ChatRemoveMemberResponse>(response)));
  }

  inviteChatMembers(
    chatId: string,
    dto: InviteChatMembersDto
  ): Observable<ChatSummaryDto> {
    return this.inviteMembers(chatId, dto);
  }

  removeChatMember(
    chatId: string,
    userId: string
  ): Observable<ChatRemoveMemberResponse> {
    return this.removeMember(chatId, userId);
  }

  uploadChatFile(
    chatId: string,
    file: File
  ): Observable<ChatMessageFileInputDto> {
    const mimeType = file.type || 'application/octet-stream';
    const request: ChatFileUploadRequest = {
      scope: 'chat',
      chatId,
      fileName: file.name,
      mimeType,
      sizeBytes: file.size,
    };

    return this.apiHttpService
      .post(this.apiEndpointsService.uploadChatFile(chatId), request)
      .pipe(
        map(response => this.unwrap<ChatFileUploadResponse>(response)),
        switchMap(upload =>
          this.apiHttpService
            .put(upload.uploadUrl, file, {
              headers: upload.headers,
              responseType: 'text',
            })
            .pipe(
              map(() => ({
                bucket: upload.bucket,
                objectKey: upload.objectKey,
                originalName: file.name,
                mimeType,
                sizeBytes: file.size,
              }))
            )
        )
      );
  }

  downloadChatFile(
    chatId: string,
    request: ChatFileDownloadRequest
  ): Observable<ChatFileDownloadResponse> {
    return this.apiHttpService
      .post(this.apiEndpointsService.downloadChatFile(chatId), request)
      .pipe(map(response => this.unwrap<ChatFileDownloadResponse>(response)));
  }

  resolveFundBySource(sourceVehicleId: string): Observable<{ id: string }> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getFundBySource(sourceVehicleId))
      .pipe(map(response => this.unwrap<{ id: string }>(response)));
  }

  getUnreadCount(): Observable<ChatUnreadCountResponse> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getChatUnreadCount())
      .pipe(map(response => this.unwrap<ChatUnreadCountResponse>(response)));
  }

  private unwrap<T>(response: unknown): T {
    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      (response as { data?: unknown }).data !== undefined
    ) {
      return (response as { data: T }).data;
    }
    return response as T;
  }
}
